import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { SECTIONS } from "@/lib/sections";

const CATEGORIES = [
  { slug: "", label: "All" },
  { slug: "workshop", label: "Workshops & Classes" },
  { slug: "exhibition", label: "Exhibitions" },
  { slug: "performance", label: "Performances" },
];

export default function Events() {
  const [params, setParams] = useSearchParams();
  const sectionFilter = params.get("section") || "";
  const categoryFilter = params.get("category") || "";

  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/events")
      .then((r) => setAllEvents(r.data))
      .catch(() => setAllEvents([]))
      .finally(() => setLoading(false));
  }, []);

  // Client-side filtering keeps counts + filter state in sync
  const matchSection = (e) => !sectionFilter || e.section === sectionFilter;
  const matchCategory = (e) => {
    if (!categoryFilter) return true;
    if (categoryFilter === "workshop") return e.category === "workshop" || e.category === "class";
    return e.category === categoryFilter;
  };
  const events = allEvents.filter((e) => matchSection(e) && matchCategory(e));

  // Counts scoped to the other active filter (e.g. section counts respect the current category)
  const sectionCounts = SECTIONS.reduce((acc, s) => {
    acc[s.slug] = allEvents.filter((e) => e.section === s.slug && matchCategory(e)).length;
    return acc;
  }, {});
  const allSectionCount = allEvents.filter(matchCategory).length;
  const categoryCounts = CATEGORIES.reduce((acc, c) => {
    if (!c.slug) {
      acc[""] = allEvents.filter(matchSection).length;
    } else if (c.slug === "workshop") {
      acc[c.slug] = allEvents.filter((e) => matchSection(e) && (e.category === "workshop" || e.category === "class")).length;
    } else {
      acc[c.slug] = allEvents.filter((e) => matchSection(e) && e.category === c.slug).length;
    }
    return acc;
  }, {});

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div data-testid="events-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-24">
      <div className="overline">Programme</div>
      <h1 className="display text-5xl md:text-7xl mt-6">All events.</h1>
      <p className="mt-6 text-muted2 max-w-2xl text-base">
        Browse exhibitions, workshops, performances and recurring classes. Book online — confirmation
        and receipt will arrive in your inbox.
      </p>

      {/* Filters */}
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <div>
          <div className="overline mb-3">Filter by space</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter("section", "")}
              className={`text-xs px-3 py-2 border ${!sectionFilter ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"}`}
              data-testid="filter-section-all"
              style={!sectionFilter ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
            >All <span className="opacity-60">({allSectionCount})</span></button>
            {SECTIONS.map((s) => {
              const n = sectionCounts[s.slug] || 0;
              const active = sectionFilter === s.slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => setFilter("section", s.slug)}
                  disabled={n === 0 && !active}
                  className={`text-xs px-3 py-2 border transition ${active ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"} ${n === 0 && !active ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`filter-section-${s.slug}`}
                  style={active ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
                >{s.label} <span className="opacity-60">({n})</span></button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="overline mb-3">Filter by type</div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const key = c.slug || "";
              const n = categoryCounts[key] || 0;
              const active = categoryFilter === c.slug;
              return (
                <button
                  key={c.slug || "all"}
                  onClick={() => setFilter("category", c.slug)}
                  disabled={n === 0 && !active}
                  className={`text-xs px-3 py-2 border transition ${active ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"} ${n === 0 && !active ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`filter-category-${c.slug || "all"}`}
                  style={active ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
                >{c.label} <span className="opacity-60">({n})</span></button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="hairline my-12" />

      {loading ? (
        <div className="text-muted2">Loading…</div>
      ) : events.length === 0 ? (
        <div className="py-24 text-center">
          <div className="font-serif text-3xl">Nothing on right now.</div>
          <p className="text-muted2 mt-3 text-sm">Try a different filter or check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {events.map((e) => <EventCard key={e.id} event={e} testIdPrefix="event" />)}
        </div>
      )}
    </div>
  );
}
